import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useUser from 'src/hooks/use-user';
import { useLoyalty } from 'src/hooks/use-loyalty';
import { Statuses } from 'src/components/loyalty/use-loyalty';
import { useLoyaltyModal } from 'src/components/modals/loyalty';
import { KioskOrderHistoryModal } from 'src/components/kiosk-pro/kiosk-pro-layout/order-history-modal';
import { ConsumerSection } from './consumer-section';
import { BaseButton, ButtonContainer, ConsumerInfoContainer, EndSessionButton } from './consumer-info.styles';

type ConsumerInfoProps = {
  createPublicContextSessionLoading: boolean;
  goToCart: () => void;
  kioskProModalLogin: () => Promise<void>;
  orderSubTotal?: number;
  startNewSession: () => void;
};

export const ConsumerInfo = observer(
  ({
    createPublicContextSessionLoading,
    goToCart,
    kioskProModalLogin,
    orderSubTotal,
    startNewSession,
  }: ConsumerInfoProps): JSX.Element => {
    const User = useUser();
    const flags = useFlags();
    const isOrderHistoryEnabled = flags['growth.ecomm.kiosk-pro-order-history.rollout'] ?? false;
    const isLoyaltyStatusEnabled = flags['growth.ecomm.kiosk-pro-loyalty-status.rollout'] ?? false;
    const [isOrderHistoryOpen, setIsOrderHistoryOpen] = useState(false);
    const { open: openLoyaltyModal } = useLoyaltyModal();
    const isUserLoggedIn = User.isLoggedIn;
    const userFirstName = User.firstName;
    const guestPhoneNumber = User.kioskGuestPhoneNumber;

    const {
      loyaltyPoints,
      loyaltyStatus,
      loyaltyProvider,
      isAlpineIQEnabled,
      isDutchiePOSLoyaltyEnabled,
      isSpringbigEnabled,
    } = useLoyalty();
    const hasLoyaltyIntegration = isAlpineIQEnabled || isDutchiePOSLoyaltyEnabled || isSpringbigEnabled;
    const hasGuestPhone = !isUserLoggedIn && !!guestPhoneNumber;

    useEffect(() => {
      if (!isUserLoggedIn) {
        setIsOrderHistoryOpen(false);
      }
    }, [isUserLoggedIn]);

    return (
      <ConsumerInfoContainer
        $isUserLoggedIn={isUserLoggedIn || hasGuestPhone}
        data-testid='kiosk-pro-consumer-info-panel'
      >
        <ConsumerSection
          createPublicContextSessionLoading={createPublicContextSessionLoading}
          guestPhoneNumber={guestPhoneNumber}
          hasGuestPhone={hasGuestPhone}
          hasLoyaltyIntegration={hasLoyaltyIntegration}
          isUserLoggedIn={isUserLoggedIn}
          kioskProModalLogin={kioskProModalLogin}
          loyaltyPoints={loyaltyPoints}
          loyaltyProvider={isLoyaltyStatusEnabled ? loyaltyProvider : undefined}
          loyaltyStatus={isLoyaltyStatusEnabled ? loyaltyStatus : undefined}
          onValidateLoyalty={
            isLoyaltyStatusEnabled && loyaltyStatus === 'validation_required'
              ? () => void openLoyaltyModal({ surface: 'kioskPro', initialStatus: Statuses.pin })
              : undefined
          }
          userFirstName={userFirstName}
        />

        <ButtonContainer data-testid='kiosk-pro-consumer-info-buttons'>
          {isOrderHistoryEnabled && isUserLoggedIn && (
            <BaseButton onClick={() => setIsOrderHistoryOpen(true)} data-testid='kiosk-pro-order-history-button'>
              Order history
            </BaseButton>
          )}
          <EndSessionButton onClick={startNewSession} data-testid='kiosk-pro-end-session-button'>
            End Session
          </EndSessionButton>
          <BaseButton onClick={goToCart} data-testid='kiosk-pro-go-to-cart-button'>
            Cart ${(orderSubTotal ?? 0).toFixed(2)}
          </BaseButton>
        </ButtonContainer>

        {isOrderHistoryEnabled && isUserLoggedIn && (
          <KioskOrderHistoryModal
            isOpen={isOrderHistoryOpen}
            onClose={() => setIsOrderHistoryOpen(false)}
            user={User}
          />
        )}
      </ConsumerInfoContainer>
    );
  }
);
